(function () {
  const SELECTOR = '[data-shows-carousel]';
  const instances = new WeakSet();

  function initGallery(card, interval) {
    const slides = Array.from(card.querySelectorAll('[data-shows-slide]'));
    if (slides.length <= 1) return null;

    const dots = Array.from(card.querySelectorAll('[data-shows-dot]'));

    let index = 0;
    let timer = null;
    const INTERVAL = Math.max(500, Number.isFinite(interval) ? interval : 5000);

    const render = () => {
      slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        const video = slide.querySelector('video');
        if (video) {
          if (isActive) {
            try {
              video.currentTime = 0;
              const p = video.play();
              if (p && typeof p.catch === 'function') p.catch(() => {});
            } catch (e) { /* no-op */ }
          } else {
            video.pause();
          }
        }
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    const advance = (delta) => {
      index = (index + delta + slides.length) % slides.length;
      render();
    };

    const goTo = (i) => {
      index = ((i % slides.length) + slides.length) % slides.length;
      render();
    };

    const start = () => {
      stop();
      timer = window.setInterval(() => advance(1), INTERVAL);
    };

    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        const i = Number.parseInt(dot.dataset.showsDotIndex || '0', 10);
        goTo(i);
        start();
      });
    });

    render();

    return { start, stop };
  }

  function init(root) {
    if (!root || instances.has(root)) return;
    instances.add(root);

    const section = root.closest('.shows-carousel') || root.parentElement;
    const viewport = root.querySelector('[data-shows-viewport]');
    const track = root.querySelector('[data-shows-track]');
    if (!viewport || !track) return;

    const cards = Array.from(track.querySelectorAll('[data-shows-card]'));
    if (cards.length === 0) return;

    const labels = section ? Array.from(section.querySelectorAll('[data-shows-label]')) : [];
    const prevBtn = section ? section.querySelector('[data-shows-prev]') : null;
    const nextBtn = section ? section.querySelector('[data-shows-next]') : null;

    // Pick initial active: first featured, else first upcoming, else 0
    let activeIndex = cards.findIndex((c) => c.dataset.isFeatured === 'true');
    if (activeIndex < 0) {
      activeIndex = cards.findIndex((c) => c.dataset.status === 'upcoming');
    }
    if (activeIndex < 0) activeIndex = 0;

    const cssHost = section || root;
    const rawInterval = window.getComputedStyle(cssHost).getPropertyValue('--sc-gallery-interval').trim();
    const galleryInterval = Number.parseInt(rawInterval, 10) || 5000;

    const galleries = cards.map((card) => initGallery(card, galleryInterval));

    const syncLabels = (status) => {
      labels.forEach((lbl) => {
        lbl.classList.toggle('is-active', lbl.dataset.showsLabel === status);
      });
    };

    const captionsContainer = section ? section.querySelector('[data-shows-captions]') : null;
    const captions = captionsContainer
      ? Array.from(captionsContainer.querySelectorAll('[data-shows-caption]'))
      : [];

    const syncCaption = (index) => {
      captions.forEach((el) => {
        const match = el.dataset.showsCaption === String(index);
        el.hidden = !match;
      });
    };

    const setActive = (i, { smooth = true } = {}) => {
      const count = cards.length;
      const next = ((i % count) + count) % count;
      activeIndex = next;

      const card = cards[next];
      if (!card) return;

      cards.forEach((c, idx) => {
        c.classList.toggle('is-active', idx === next);
      });

      syncLabels(card.dataset.status);
      syncCaption(next);

      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const scrollTarget = Math.max(0, cardCenter - viewport.clientWidth / 2);
      viewport.scrollTo({
        left: scrollTarget,
        behavior: smooth ? 'smooth' : 'auto',
      });

      galleries.forEach((g, idx) => {
        if (!g) return;
        if (idx === next) g.start();
        else g.stop();
      });
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', () => setActive(activeIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => setActive(activeIndex + 1));
    }

    labels.forEach((lbl) => {
      lbl.addEventListener('click', () => {
        const status = lbl.dataset.showsLabel;
        const idx = cards.findIndex((c) => c.dataset.status === status);
        if (idx >= 0) setActive(idx);
      });
    });

    viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActive(activeIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActive(activeIndex + 1);
      }
    });

    let scrollTimer = null;
    viewport.addEventListener('scroll', () => {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        const center = viewport.scrollLeft + viewport.clientWidth / 2;
        let nearest = 0;
        let nearestDist = Infinity;
        cards.forEach((card, idx) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const dist = Math.abs(cardCenter - center);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = idx;
          }
        });
        if (nearest !== activeIndex) {
          setActive(nearest);
        }
      }, 200);
    }, { passive: true });

    // Initial placement (no animation)
    const placeInitial = () => setActive(activeIndex, { smooth: false });
    if (document.readyState === 'complete') {
      placeInitial();
    } else {
      window.addEventListener('load', placeInitial, { once: true });
      requestAnimationFrame(placeInitial);
    }

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => setActive(activeIndex, { smooth: false }), 150);
    });
  }

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const el = e.target && e.target.querySelector ? e.target.querySelector(SELECTOR) : null;
    if (el) init(el);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const card = e.target;
    if (!card || !card.matches) return;
    if (card.matches('[data-shows-card]')) {
      const root = card.closest(SELECTOR);
      if (!root) return;
      init(root);
      const cards = Array.from(root.querySelectorAll('[data-shows-card]'));
      const idx = cards.indexOf(card);
      if (idx < 0) return;
      const section = root.closest('.shows-carousel') || root.parentElement;
      const viewport = root.querySelector('[data-shows-viewport]');
      const labels = section ? Array.from(section.querySelectorAll('[data-shows-label]')) : [];
      cards.forEach((c, i) => c.classList.toggle('is-active', i === idx));
      if (viewport) {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        viewport.scrollTo({
          left: Math.max(0, cardCenter - viewport.clientWidth / 2),
          behavior: 'smooth',
        });
      }
      labels.forEach((lbl) => {
        lbl.classList.toggle('is-active', lbl.dataset.showsLabel === card.dataset.status);
      });
    }
  });
})();
